'use client';

import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSchema } from '../../contexts/SchemaContext';
import { Column } from '../../types';
import { MagnifyingGlass, Check, Database, Table } from '@phosphor-icons/react';

interface ColumnSelectorProps {
  value?: string;
  onChange: (columnName: string, columnInfo?: Column) => void;
  placeholder?: string;
  filterType?: 'string' | 'number' | 'date' | 'all';
  showType?: boolean;
  // 可选：传入自定义列列表，如果不传则使用所有可用列
  columns?: Array<{ column: Column; source: { nodeId: string; nodeType: string; nodeName: string } }>;
}

export function ColumnSelector({
  value,
  onChange,
  placeholder = '选择列...',
  filterType = 'all',
  showType = true,
  columns: customColumns,
}: ColumnSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { availableSchemas, getAllAvailableColumns } = useSchema();

  const availableColumns = useMemo(() => {
    // 如果传入了自定义列列表，使用它；否则使用所有可用列
    const columns = customColumns ?? getAllAvailableColumns();
    if (filterType === 'all') return columns;
    
    return columns.filter(({ column }) => {
      if (filterType === 'string') return column.type === 'string';
      if (filterType === 'number') return column.type === 'number' || column.type === 'datetime';
      if (filterType === 'date') return column.type === 'date' || column.type === 'datetime';
      return true;
    });
  }, [getAllAvailableColumns, filterType, customColumns]);

  const filteredColumns = useMemo(() => {
    if (!searchQuery) return availableColumns;
    const query = searchQuery.toLowerCase();
    return availableColumns.filter(
      ({ column }) =>
        column.name.toLowerCase().includes(query) ||
        column.semanticType?.toLowerCase().includes(query)
    );
  }, [availableColumns, searchQuery]);

  // Group by source
  const groupedColumns = useMemo(() => {
    const groups: Record<string, typeof filteredColumns> = {};
    filteredColumns.forEach(item => {
      const sourceId = item.source.nodeId;
      if (!groups[sourceId]) {
        groups[sourceId] = [];
      }
      groups[sourceId].push(item);
    });
    return groups;
  }, [filteredColumns]);

  const selectedColumn = useMemo(() => {
    return availableColumns.find(({ column }) => column.name === value);
  }, [availableColumns, value]);

  if (availableSchemas.length === 0) {
    return (
      <div className="w-full px-3 py-2 bg-zinc-50 border border-zinc-200 rounded-lg text-sm text-zinc-400">
        请先上传数据文件或添加数据源节点
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Trigger */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`
          w-full flex items-center justify-between px-3 py-2 
          bg-white border rounded-lg text-sm
          transition-all duration-200
          ${isOpen 
            ? 'border-emerald-500 ring-2 ring-emerald-500/20' 
            : 'border-zinc-200 hover:border-zinc-300'
          }
        `}
      >
        <div className="flex items-center gap-2 min-w-0">
          {selectedColumn ? (
            <>
              <span className="font-medium text-zinc-900 truncate">
                {selectedColumn.column.name}
              </span>
              {showType && (
                <span className="text-xs text-zinc-400">
                  ({selectedColumn.column.type})
                </span>
              )}
            </>
          ) : (
            <span className="text-zinc-400">{placeholder}</span>
          )}
        </div>
        <MagnifyingGlass className="w-4 h-4 text-zinc-400 flex-shrink-0" />
      </button>

      {/* Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-40"
              onClick={() => setIsOpen(false)}
            />

            {/* Panel */}
            <motion.div
              initial={{ opacity: 0, y: -10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.95 }}
              transition={{ type: 'spring', stiffness: 300, damping: 25 }}
              className="absolute top-full left-0 right-0 mt-2 z-50 bg-white rounded-xl border border-zinc-200 shadow-xl shadow-zinc-900/10 overflow-hidden"
            >
              {/* Search */}
              <div className="p-3 border-b border-zinc-100">
                <div className="relative">
                  <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="搜索列名..."
                    className="w-full pl-9 pr-3 py-2 bg-zinc-50 border border-zinc-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                    autoFocus
                  />
                </div>
              </div>

              {/* Column List */}
              <div className="max-h-64 overflow-y-auto">
                {Object.entries(groupedColumns).length === 0 ? (
                  <div className="p-4 text-center text-sm text-zinc-400">
                    未找到匹配的列
                  </div>
                ) : (
                  Object.entries(groupedColumns).map(([sourceId, items]) => (
                    <div key={sourceId} className="border-b border-zinc-100 last:border-0">
                      {/* Source Header */}
                      <div className="px-3 py-2 bg-zinc-50 flex items-center gap-2">
                        {items[0].source.nodeType === 'data_source' ? (
                          <Database className="w-3.5 h-3.5 text-emerald-500" />
                        ) : (
                          <Table className="w-3.5 h-3.5 text-blue-500" />
                        )}
                        <span className="text-xs font-medium text-zinc-600">
                          {items[0].source.nodeName}
                        </span>
                      </div>

                      {/* Columns */}
                      <div className="p-1">
                        {items.map(({ column }) => (
                          <button
                            key={`${sourceId}-${column.name}`}
                            type="button"
                            onClick={() => {
                              onChange(column.name, column);
                              setIsOpen(false);
                              setSearchQuery('');
                            }}
                            className={`
                              w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm
                              transition-colors
                              ${value === column.name
                                ? 'bg-emerald-50 text-emerald-700'
                                : 'hover:bg-zinc-50 text-zinc-700'
                              }
                            `}
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="font-medium truncate">
                                {column.name}
                              </span>
                              {showType && (
                                <span className="text-xs text-zinc-400">
                                  ({column.type})
                                </span>
                              )}
                            </div>
                            {value === column.name && (
                              <Check className="w-4 h-4 text-emerald-500" weight="bold" />
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Footer */}
              <div className="px-3 py-2 bg-zinc-50 border-t border-zinc-100 text-xs text-zinc-400">
                共 {availableColumns.length} 个可用列
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

// Multi-column selector for operations that need multiple columns
interface MultiColumnSelectorProps {
  values: string[];
  onChange: (columnNames: string[]) => void;
  maxSelections?: number;
  columns?: Array<{ column: Column; source: { nodeId: string; nodeType: string; nodeName: string } }>;
}

export function MultiColumnSelector({
  values,
  onChange,
  maxSelections,
  columns: customColumns,
}: MultiColumnSelectorProps) {
  const { getAllAvailableColumns } = useSchema();
  const availableColumns = customColumns ?? getAllAvailableColumns();

  const toggleColumn = (columnName: string) => {
    if (values.includes(columnName)) {
      onChange(values.filter(v => v !== columnName));
    } else if (!maxSelections || values.length < maxSelections) {
      onChange([...values, columnName]);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {availableColumns.map(({ column, source }) => (
          <button
            key={`${source.nodeId}-${column.name}`}
            type="button"
            onClick={() => toggleColumn(column.name)}
            disabled={maxSelections !== undefined && values.length >= maxSelections && !values.includes(column.name)}
            className={`
              px-3 py-1.5 rounded-lg text-sm border transition-all
              ${values.includes(column.name)
                ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                : 'bg-white border-zinc-200 text-zinc-600 hover:border-zinc-300'
              }
              ${maxSelections !== undefined && values.length >= maxSelections && !values.includes(column.name)
                ? 'opacity-50 cursor-not-allowed'
                : ''
              }
            `}
          >
            {column.name}
          </button>
        ))}
      </div>
      {values.length > 0 && (
        <div className="space-y-2">
          <div className="text-xs text-zinc-400">
            已选择 {values.length} 个列
            {maxSelections && ` / 最多 ${maxSelections} 个`}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {values.map((columnName, index) => (
              <span
                key={columnName}
                className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-50 border border-emerald-100 rounded-md text-xs text-emerald-700"
              >
                <span className="text-emerald-500 font-medium">{index + 1}.</span>
                {columnName}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
